import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { APIRequestStatus } from "common-ts/dist/models/APIRequestStatus";
import { fetchBroadcastCampaignList } from "./FinTrackerListRedux";

// Broadcast admin operations route through the livestream dispatcher with a
// { broadcastAction, payload } body (same pattern as AccountingRedux).
const BROADCAST_ENDPOINT =
    "https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/livestream-3820238";

// Posts a broadcast action and returns the handler's response body, tolerating
// validation 400s (Lambda-proxy turns statusCode 400 into an axios rejection).
export async function callBroadcast(broadcastAction: string, payload: any = {}) {
    try {
        const response = await axios({
            method: "post",
            url: BROADCAST_ENDPOINT,
            data: JSON.stringify({ broadcastAction, payload }),
        });
        return response.data;
    } catch (error: any) {
        if (error?.response?.data) {
            return error.response.data;
        }
        throw error;
    }
}

const initialState: any = {
    // Campaign detail (dashboard row -> detail view). The campaign LIST stays in
    // FinTrackerListRedux.broadcastCampaignList (existing GET endpoint).
    selectedCampaign: null,
    selectedCampaignStatus: APIRequestStatus.Idle,

    // Live audience preview for the wizard's Audiens & Skor step.
    audiencePreview: null, // { totalMatched, topSample, scoreDataUpdatedAt, usedFallbackScan, fatigueBreakdown, opportunity }
    audiencePreviewStatus: APIRequestStatus.Idle,

    // Create/update result for the wizard's submit step.
    saveStatus: APIRequestStatus.Idle,
    lastSaveResult: null, // { success, message, data }

    // Start/stop/pause/resume dispatches.
    controlStatus: APIRequestStatus.Idle,
    lastControlResult: null,

    statsRefreshStatus: APIRequestStatus.Idle,

    // Saved audiences (named reusable filters).
    savedAudiences: [], // [{ audienceId, name, filter, weights, audienceType, createdAt, updatedAt }]
    savedAudiencesStatus: APIRequestStatus.Idle,
    audienceSaveStatus: APIRequestStatus.Idle,
    lastAudienceSaveResult: null,

    // Code-defined SYS-* audiences (ride along on AUDIENCE_LIST).
    systemAudiences: [], // [{ audienceId, name, isSystem, filter }]

    // Saved blast settings (named blasting methodology: batch ramp + stop
    // criteria + coupon-expiry rule + assumed margin; NO sendTimeLocal).
    blastSettings: [], // [{ settingId, name, batchConfig, stopCriteria, couponExpiry, assumedMarginRatio, createdAt, updatedAt }]
    blastSettingsStatus: APIRequestStatus.Idle,
    blastSettingSaveStatus: APIRequestStatus.Idle,

    // Connected WhatsApp accounts (credentials never reach the browser —
    // rows carry credentialsMeta tails only). Each row is tied to a provider
    // (WATI, WATZAP, future vendors) with its own credential schema.
    waAccounts: [], // [{ accountId, name, provider, channelClass, channelType, phoneNumber, active, isDefault, credentialsMeta, updatedAt }]
    waAccountsStatus: APIRequestStatus.Idle,
    waAccountSaveStatus: APIRequestStatus.Idle,
    // Provider catalog (rides along on ACCOUNT_LIST): [{ providerKey, label,
    // channelClass, channelKey, credentialFields: [{key, label, secret, required}] }]
    waProviders: [],

    // Saved broadcast message templates (whole Funnel & Kreatif stage,
    // frontend-draft-shaped funnelSteps).
    messageTemplates: [], // [{ templateId, name, funnelSteps, createdAt, updatedAt }]
    messageTemplatesStatus: APIRequestStatus.Idle,
    messageTemplateSaveStatus: APIRequestStatus.Idle,

    // Fatigue/opportunity stats per audience (system + saved).
    audienceStats: null, // { audiences: [{ audienceId, name, isSystem, filter, total, fatigueBreakdown, opportunity }], scoreDataUpdatedAt, usedFallbackScan }
    audienceStatsStatus: APIRequestStatus.Idle,
};

export const fetchCampaignDetail = createAsyncThunk(
    "broadcast/fetchCampaignDetail",
    async (arg: { campaignId: string }) => {
        const data = await callBroadcast("CAMPAIGN_GET", arg);
        return data;
    }
);

export const fetchSavedAudiences = createAsyncThunk("broadcast/fetchSavedAudiences", async () => {
    const data = await callBroadcast("AUDIENCE_LIST");
    return data;
});

// Member count + fatigue breakdown + opportunity for every system and saved
// audience (one shared score-export load on the backend).
export const fetchAudienceStats = createAsyncThunk("broadcast/fetchAudienceStats", async () => {
    const data = await callBroadcast("AUDIENCE_STATS");
    return data;
});

export const saveAudience = createAsyncThunk(
    "broadcast/saveAudience",
    async (arg: { audienceId?: string; name: string; filter: any; weights?: any; audienceType?: string }, thunkAPI) => {
        const data = await callBroadcast("AUDIENCE_SAVE", arg);
        if (data?.success) thunkAPI.dispatch(fetchSavedAudiences());
        return data;
    }
);

export const deleteAudience = createAsyncThunk(
    "broadcast/deleteAudience",
    async (arg: { audienceId: string }, thunkAPI) => {
        const data = await callBroadcast("AUDIENCE_DELETE", arg);
        if (data?.success) thunkAPI.dispatch(fetchSavedAudiences());
        return data;
    }
);

export const fetchBlastSettings = createAsyncThunk("broadcast/fetchBlastSettings", async () => {
    const data = await callBroadcast("SETTING_LIST");
    return data;
});

export const saveBlastSetting = createAsyncThunk(
    "broadcast/saveBlastSetting",
    async (
        arg: { settingId?: string; name: string; batchConfig: any; stopCriteria: any; couponExpiry: any; assumedMarginRatio?: number },
        thunkAPI
    ) => {
        const data = await callBroadcast("SETTING_SAVE", arg);
        if (data?.success) thunkAPI.dispatch(fetchBlastSettings());
        return data;
    }
);

export const deleteBlastSetting = createAsyncThunk(
    "broadcast/deleteBlastSetting",
    async (arg: { settingId: string }, thunkAPI) => {
        const data = await callBroadcast("SETTING_DELETE", arg);
        if (data?.success) thunkAPI.dispatch(fetchBlastSettings());
        return data;
    }
);

export const fetchWaAccounts = createAsyncThunk("broadcast/fetchWaAccounts", async () => {
    const data = await callBroadcast("ACCOUNT_LIST");
    return data;
});

export const saveWaAccount = createAsyncThunk(
    "broadcast/saveWaAccount",
    async (
        arg: { accountId?: string; name: string; provider?: string; phoneNumber?: string; active?: boolean; isDefault?: boolean; credentials?: any },
        thunkAPI
    ) => {
        const data = await callBroadcast("ACCOUNT_SAVE", arg);
        if (data?.success) thunkAPI.dispatch(fetchWaAccounts());
        return data;
    }
);

export const deleteWaAccount = createAsyncThunk(
    "broadcast/deleteWaAccount",
    async (arg: { accountId: string }, thunkAPI) => {
        const data = await callBroadcast("ACCOUNT_DELETE", arg);
        if (data?.success) thunkAPI.dispatch(fetchWaAccounts());
        return data;
    }
);

export const fetchMessageTemplates = createAsyncThunk("broadcast/fetchMessageTemplates", async () => {
    const data = await callBroadcast("TEMPLATE_LIST");
    return data;
});

export const saveMessageTemplate = createAsyncThunk(
    "broadcast/saveMessageTemplate",
    async (arg: { templateId?: string; name: string; funnelSteps: any[] }, thunkAPI) => {
        const data = await callBroadcast("TEMPLATE_SAVE", arg);
        if (data?.success) thunkAPI.dispatch(fetchMessageTemplates());
        return data;
    }
);

export const deleteMessageTemplate = createAsyncThunk(
    "broadcast/deleteMessageTemplate",
    async (arg: { templateId: string }, thunkAPI) => {
        const data = await callBroadcast("TEMPLATE_DELETE", arg);
        if (data?.success) thunkAPI.dispatch(fetchMessageTemplates());
        return data;
    }
);

export const fetchAudiencePreview = createAsyncThunk(
    "broadcast/fetchAudiencePreview",
    async (arg: { audienceType?: string; filter?: any; scoring?: any }) => {
        const data = await callBroadcast("AUDIENCE_PREVIEW", arg);
        return data;
    }
);

export const saveCampaign = createAsyncThunk(
    "broadcast/saveCampaign",
    async (arg: { isUpdate: boolean; campaign: any }, thunkAPI) => {
        const data = await callBroadcast(arg.isUpdate ? "CAMPAIGN_UPDATE" : "CAMPAIGN_CREATE", arg.campaign);
        if (data?.success) {
            thunkAPI.dispatch(fetchBroadcastCampaignList());
        }
        return data;
    }
);

// action: 'START' | 'STOP' | 'PAUSE' | 'RESUME'
export const controlCampaign = createAsyncThunk(
    "broadcast/controlCampaign",
    async (arg: { campaignId: string; action: string }, thunkAPI) => {
        const data = await callBroadcast("CAMPAIGN_" + arg.action, { campaignId: arg.campaignId });
        // The orchestrator is invoked async; give it a beat before refetching state.
        setTimeout(() => {
            thunkAPI.dispatch(fetchCampaignDetail({ campaignId: arg.campaignId }));
            thunkAPI.dispatch(fetchBroadcastCampaignList());
        }, 2500);
        return data;
    }
);

// Mid-flight reconfigure: patch = { nextBatchSize?, maxBatchSize?,
// maxTotalRecipients?, sendTimeLocal?, evaluationDelayHours?, endDateTime? }.
// Backend invokes the orchestrator synchronously and returns { reconfigured,
// appliedFields, audienceTotal }.
export const reconfigureCampaign = createAsyncThunk(
    "broadcast/reconfigureCampaign",
    async (arg: { campaignId: string; patch: any }, thunkAPI) => {
        const data = await callBroadcast("CAMPAIGN_RECONFIGURE", { campaignId: arg.campaignId, patch: arg.patch });
        if (data?.success) {
            thunkAPI.dispatch(fetchCampaignDetail({ campaignId: arg.campaignId }));
            thunkAPI.dispatch(fetchBroadcastCampaignList());
        }
        return data;
    }
);

export const refreshCampaignStats = createAsyncThunk(
    "broadcast/refreshCampaignStats",
    async (arg: { campaignId: string }, thunkAPI) => {
        const data = await callBroadcast("CAMPAIGN_STATS_REFRESH", arg);
        if (data?.success) {
            thunkAPI.dispatch(fetchCampaignDetail({ campaignId: arg.campaignId }));
        }
        return data;
    }
);

const slice = createSlice({
    name: "broadcast",
    initialState,
    reducers: {
        clearSaveResult(state) {
            state.lastSaveResult = null;
            state.saveStatus = APIRequestStatus.Idle;
        },
        clearAudiencePreview(state) {
            state.audiencePreview = null;
            state.audiencePreviewStatus = APIRequestStatus.Idle;
        },
        clearSelectedCampaign(state) {
            state.selectedCampaign = null;
            state.selectedCampaignStatus = APIRequestStatus.Idle;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchCampaignDetail.pending, (state) => {
                state.selectedCampaignStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchCampaignDetail.fulfilled, (state, action) => {
                // Same rule as the audience preview: an error body still resolves the
                // thunk, so only a payload with success + data counts as Success.
                state.selectedCampaignStatus = action.payload?.success && action.payload?.data
                    ? APIRequestStatus.Success
                    : APIRequestStatus.Failure;
                state.selectedCampaign = action.payload?.data ?? null;
            })
            .addCase(fetchCampaignDetail.rejected, (state) => {
                state.selectedCampaignStatus = APIRequestStatus.Failure;
            })
            .addCase(fetchAudiencePreview.pending, (state) => {
                state.audiencePreviewStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchAudiencePreview.fulfilled, (state, action) => {
                // A dispatcher error body (validation 400 / not-yet-deployed 5xx) resolves
                // the thunk too — only a real success payload counts as Success.
                state.audiencePreviewStatus = action.payload?.success
                    ? APIRequestStatus.Success
                    : APIRequestStatus.Failure;
                state.audiencePreview = action.payload?.data ?? null;
            })
            .addCase(fetchAudiencePreview.rejected, (state) => {
                state.audiencePreviewStatus = APIRequestStatus.Failure;
            })
            .addCase(saveCampaign.pending, (state) => {
                state.saveStatus = APIRequestStatus.RequestInProgress;
                state.lastSaveResult = null;
            })
            .addCase(saveCampaign.fulfilled, (state, action) => {
                state.saveStatus = action.payload?.success ? APIRequestStatus.Success : APIRequestStatus.Failure;
                state.lastSaveResult = action.payload ?? null;
            })
            .addCase(saveCampaign.rejected, (state) => {
                state.saveStatus = APIRequestStatus.Failure;
                state.lastSaveResult = { success: false, message: "Request gagal" };
            })
            .addCase(controlCampaign.pending, (state) => {
                state.controlStatus = APIRequestStatus.RequestInProgress;
                state.lastControlResult = null;
            })
            .addCase(controlCampaign.fulfilled, (state, action) => {
                state.controlStatus = action.payload?.success ? APIRequestStatus.Success : APIRequestStatus.Failure;
                state.lastControlResult = action.payload ?? null;
            })
            .addCase(controlCampaign.rejected, (state) => {
                state.controlStatus = APIRequestStatus.Failure;
                state.lastControlResult = { success: false, message: "Request gagal" };
            })
            .addCase(refreshCampaignStats.pending, (state) => {
                state.statsRefreshStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(refreshCampaignStats.fulfilled, (state) => {
                state.statsRefreshStatus = APIRequestStatus.Success;
            })
            .addCase(refreshCampaignStats.rejected, (state) => {
                state.statsRefreshStatus = APIRequestStatus.Failure;
            })
            .addCase(fetchSavedAudiences.pending, (state) => {
                state.savedAudiencesStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchSavedAudiences.fulfilled, (state, action) => {
                state.savedAudiencesStatus = APIRequestStatus.Success;
                state.savedAudiences = action.payload?.data?.audiences ?? [];
                state.systemAudiences = action.payload?.data?.systemAudiences ?? [];
            })
            .addCase(fetchSavedAudiences.rejected, (state) => {
                state.savedAudiencesStatus = APIRequestStatus.Failure;
            })
            .addCase(fetchBlastSettings.pending, (state) => {
                state.blastSettingsStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchBlastSettings.fulfilled, (state, action) => {
                state.blastSettingsStatus = action.payload?.success
                    ? APIRequestStatus.Success
                    : APIRequestStatus.Failure;
                state.blastSettings = action.payload?.data?.settings ?? state.blastSettings;
            })
            .addCase(fetchBlastSettings.rejected, (state) => {
                state.blastSettingsStatus = APIRequestStatus.Failure;
            })
            .addCase(saveBlastSetting.pending, (state) => {
                state.blastSettingSaveStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(saveBlastSetting.fulfilled, (state, action) => {
                // Dispatcher error bodies resolve as fulfilled — check success.
                state.blastSettingSaveStatus = action.payload?.success
                    ? APIRequestStatus.Success
                    : APIRequestStatus.Failure;
            })
            .addCase(saveBlastSetting.rejected, (state) => {
                state.blastSettingSaveStatus = APIRequestStatus.Failure;
            })
            .addCase(fetchWaAccounts.pending, (state) => {
                state.waAccountsStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchWaAccounts.fulfilled, (state, action) => {
                state.waAccountsStatus = action.payload?.success
                    ? APIRequestStatus.Success
                    : APIRequestStatus.Failure;
                state.waAccounts = action.payload?.data?.accounts ?? state.waAccounts;
                state.waProviders = action.payload?.data?.providers ?? state.waProviders;
            })
            .addCase(fetchWaAccounts.rejected, (state) => {
                state.waAccountsStatus = APIRequestStatus.Failure;
            })
            .addCase(saveWaAccount.pending, (state) => {
                state.waAccountSaveStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(saveWaAccount.fulfilled, (state, action) => {
                state.waAccountSaveStatus = action.payload?.success
                    ? APIRequestStatus.Success
                    : APIRequestStatus.Failure;
            })
            .addCase(saveWaAccount.rejected, (state) => {
                state.waAccountSaveStatus = APIRequestStatus.Failure;
            })
            .addCase(fetchMessageTemplates.pending, (state) => {
                state.messageTemplatesStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchMessageTemplates.fulfilled, (state, action) => {
                state.messageTemplatesStatus = action.payload?.success
                    ? APIRequestStatus.Success
                    : APIRequestStatus.Failure;
                state.messageTemplates = action.payload?.data?.templates ?? state.messageTemplates;
            })
            .addCase(fetchMessageTemplates.rejected, (state) => {
                state.messageTemplatesStatus = APIRequestStatus.Failure;
            })
            .addCase(saveMessageTemplate.pending, (state) => {
                state.messageTemplateSaveStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(saveMessageTemplate.fulfilled, (state, action) => {
                state.messageTemplateSaveStatus = action.payload?.success
                    ? APIRequestStatus.Success
                    : APIRequestStatus.Failure;
            })
            .addCase(saveMessageTemplate.rejected, (state) => {
                state.messageTemplateSaveStatus = APIRequestStatus.Failure;
            })
            .addCase(fetchAudienceStats.pending, (state) => {
                state.audienceStatsStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchAudienceStats.fulfilled, (state, action) => {
                // Dispatcher error bodies resolve as fulfilled — check success.
                state.audienceStatsStatus = action.payload?.success
                    ? APIRequestStatus.Success
                    : APIRequestStatus.Failure;
                state.audienceStats = action.payload?.success ? (action.payload?.data ?? null) : state.audienceStats;
            })
            .addCase(fetchAudienceStats.rejected, (state) => {
                state.audienceStatsStatus = APIRequestStatus.Failure;
            })
            .addCase(saveAudience.pending, (state) => {
                state.audienceSaveStatus = APIRequestStatus.RequestInProgress;
                state.lastAudienceSaveResult = null;
            })
            .addCase(saveAudience.fulfilled, (state, action) => {
                state.audienceSaveStatus = action.payload?.success ? APIRequestStatus.Success : APIRequestStatus.Failure;
                state.lastAudienceSaveResult = action.payload ?? null;
            })
            .addCase(saveAudience.rejected, (state) => {
                state.audienceSaveStatus = APIRequestStatus.Failure;
                state.lastAudienceSaveResult = { success: false, message: "Request gagal" };
            });
    },
});

export const { clearSaveResult, clearAudiencePreview, clearSelectedCampaign } = slice.actions;

export default slice.reducer;
